import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import api from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import "./Onboarding.css";

function SelectCompanyPlan() {
  const navigate = useNavigate();
  const { user, onboardingStage, setOnboarding, refreshUser, logout } = useAuth();
  const [plans, setPlans] = useState([]);
  const [currentEmployeeCount, setCurrentEmployeeCount] = useState(0);
  const [loadingPlanId, setLoadingPlanId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (onboardingStage && onboardingStage !== "select_plan") {
      navigate("/dashboard");
      return;
    }

    fetchPlans();
  }, [onboardingStage, navigate]);

  const ensureRazorpayScript = async () => {
    if (window.Razorpay) return true;

    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const fetchPlans = async () => {
    try {
      const response = await api.get("/onboarding/plans");
      setPlans(response.data?.data?.plans || []);
      setCurrentEmployeeCount(response.data?.data?.current_employee_count || 0);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to load plans",
        text: "Please refresh and try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const finalizeOnboarding = async (onboarding) => {
    setOnboarding(onboarding);
    await refreshUser();
    await logout();
    navigate("/login");
  };

  const handleFreePlan = async (plan) => {
    setLoadingPlanId(plan.id);

    try {
      const response = await api.post("/onboarding/plans/select-free", {
        plan_id: plan.id,
      });

      Swal.fire({
        icon: "success",
        title: "Plan activated",
        text: "Plan activated successfully. Please login to continue.",
        timer: 1800,
        showConfirmButton: false,
      });

      await finalizeOnboarding(response.data?.data?.onboarding);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Activation failed",
        text: error.response?.data?.message || "Unable to activate free plan.",
      });
    } finally {
      setLoadingPlanId(null);
    }
  };

  const handlePaidPlan = async (plan) => {
    setLoadingPlanId(plan.id);

    try {
      const razorpayLoaded = await ensureRazorpayScript();
      if (!razorpayLoaded) {
        throw new Error("Razorpay SDK failed to load.");
      }

      const response = await api.post("/onboarding/plans/create-subscription", {
        plan_id: plan.id,
      });

      const payload = response.data?.data || {};

      const options = {
        key: payload.razorpay_key,
        subscription_id: payload.subscription_id,
        name: user?.companyDetails?.company_name || payload.plan?.name || "Cedur Plan",
        description: payload.plan?.name || "Cedur Subscription",
        handler: async (rzpResponse) => {
          try {
            const verifyResponse = await api.post("/onboarding/plans/verify-subscription", {
              razorpay_subscription_id: rzpResponse.razorpay_subscription_id,
            });

            Swal.fire({
              icon: "success",
              title: "Plan activated",
              text: "Subscription activated successfully. Please login to continue.",
              timer: 1800,
              showConfirmButton: false,
            });

            await finalizeOnboarding(verifyResponse.data?.data?.onboarding);
          } catch (verifyError) {
            Swal.fire({
              icon: "error",
              title: "Verification failed",
              text: verifyError.response?.data?.message || "Unable to verify subscription.",
            });
          }
        },
        modal: {
          ondismiss: () => {
            Swal.fire({
              icon: "info",
              title: "Payment not completed",
              text: "You can resume plan selection whenever you're ready.",
            });
          },
        },
        prefill: {
          name: `${user?.first_name || ""} ${user?.last_name || ""}`.trim(),
          email: user?.email || "",
          contact: user?.phone_number || "",
        },
        theme: {
          color: "#635bff",
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Payment setup failed",
        text: error.response?.data?.message || error.message || "Unable to start plan payment.",
      });
    } finally {
      setLoadingPlanId(null);
    }
  };

  const handlePlanSelect = async (plan) => {
    if (((plan.estimated_amount ?? plan.plan_price) || 0) <= 0) {
      await handleFreePlan(plan);
      return;
    }

    await handlePaidPlan(plan);
  };

  if (!user || onboardingStage !== "select_plan") {
    return null;
  }

  return (
    <div className="onboarding-shell">
      <div className="onboarding-brandbar">
        <div className="onboarding-brand">
          <img
            src="https://cedurnow.com/assets/img/logos/new-logo.png"
            alt="Cedur"
            className="onboarding-brand-logo"
          />
          <div className="onboarding-brand-copy">
            <span className="onboarding-brand-kicker">Cedur Onboarding</span>
            <h1>Choose your company plan</h1>
          </div>
        </div>
        <div className="onboarding-brand-note">
          <span>Final step</span>
          <small>Pick a plan now and you’ll be ready to log in and start using Cedur.</small>
        </div>
      </div>

      <div className="onboarding-card">
        <div className="onboarding-header">
          <div>
            <div className="onboarding-step">Step 4 of 4</div>
            <h2>Select Company Plan</h2>
            <p>Choose the plan that fits your team. Paid plans continue into Razorpay checkout.</p>
          </div>
          <span className="onboarding-badge">{user?.companyDetails?.company_name || "Company setup"}</span>
        </div>

        <div className="onboarding-body">
          <div className="onboarding-alert">
            Free plans activate immediately. Paid plans continue through a secure Razorpay subscription checkout.
          </div>

          {loading ? (
            <p>Loading plans...</p>
          ) : (
            <div className="onboarding-plan-grid">
              {plans.map((plan) => (
                <div
                  key={plan.id}
                  className={`onboarding-plan-card ${Number(plan.plan_price) === 0 ? "highlighted" : ""}`}
                >
                  <div>
                    <h3>{plan.plan_name}</h3>
                    <p>{plan.plan_details}</p>
                  </div>

                  <div className="plan-price">
                    {Number(plan.estimated_amount ?? plan.base_fee ?? plan.plan_price) === 0
                      ? "Free"
                      : `Rs. ${Number(plan.estimated_amount ?? plan.base_fee ?? plan.plan_price).toLocaleString("en-IN")}`}
                    <small>{plan.plan_validity ? ` / ${plan.plan_validity}` : ""}</small>
                  </div>

                  <div className="plan-meta">
                    <span>{plan.included_employees || 0} included | ceiling {plan.employee_ceiling || "-"}</span>
                    <span>{Number(plan.estimated_amount ?? plan.plan_price) === 0 ? "Instant activation" : "Razorpay checkout"}</span>
                  </div>

                  <div className="plan-meta">
                    <span>{`Current team: ${currentEmployeeCount}`}</span>
                    <span>{`Add-on: Rs. ${Number(plan.add_on_rate || 0).toLocaleString("en-IN")}/employee`}</span>
                  </div>

                  <ul className="plan-feature-list">
                    {(plan.features || []).slice(0, 8).map((feature) => (
                      <li key={feature.id || feature.title}>
                        <span>{feature.title || feature.value || "Plan feature"}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    type="button"
                    className="onboarding-btn primary"
                    onClick={() => handlePlanSelect(plan)}
                    disabled={loadingPlanId === plan.id}
                  >
                    {loadingPlanId === plan.id
                      ? "Please wait..."
                      : Number(plan.estimated_amount ?? plan.plan_price) === 0
                        ? "Activate Free Plan"
                        : "Continue to Payment"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="onboarding-footer">
        <span>Secure checkout powered by Razorpay for paid plans.</span>
        <span>© 2026 Cedur Enterprises Private Limited. All rights reserved.</span>
      </div>
    </div>
  );
}

export default SelectCompanyPlan;
